import { useState, useEffect, useRef } from 'react';
import { Heart, MapPin, Briefcase, ArrowLeft, Crown, User } from 'lucide-react';
import { Loader } from './ui/Loader';
import { API_BASE } from '../utils/supabase/info';
import { fetchWithAuth } from '../utils/api';
import { handleApiError } from '../utils/api-error-handler';
import { devLog } from '../utils/logger';
import { canUploadVideo } from '../utils/accessPolicy';
import { PageShell } from './layout/PageShell';


interface FavoritesPageProps {
  accessToken: string;
  onBack: () => void;
  onViewProfile: (userId: string) => void;
  /** When true, page is visible; refetch so newly added favorites show. */
  isVisible?: boolean;
  /** Called when user taps "Utforsk talenter" in empty state; defaults to onBack. */
  onExploreTalents?: () => void;
  /** When true (desktop modal), hide the sub-page header; modal chrome shows back button. */
  hideHeader?: boolean;
  onNavigateToSubscription?: () => void;
}

export function FavoritesPage({ accessToken, onBack, onViewProfile, isVisible = true, onExploreTalents, hideHeader = false, onNavigateToSubscription }: FavoritesPageProps) {
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // iOS PWA: JS-based sticky sub-page header (position:sticky broken in standalone)
  const stickySentinelRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyPlaceholderRef = useRef<HTMLDivElement>(null);
  const subPageContentRef = useRef<HTMLDivElement>(null);

  // iOS PWA: when page becomes visible, scroll .sub-page-content to top (persistent mount keeps scroll position)
  useEffect(() => {
    if (!isVisible) return;
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const el = subPageContentRef.current;
    if (!el) return;
    const id = requestAnimationFrame(() => {
      el.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(id);
  }, [isVisible]);

  useEffect(() => {
    const isIosPwa = typeof document !== 'undefined' && document.documentElement.classList.contains('ios-pwa');
    if (!isIosPwa) return;
    const sentinel = stickySentinelRef.current;
    const bar = stickyHeaderRef.current;
    const placeholder = stickyPlaceholderRef.current;
    if (!sentinel || !bar || !placeholder) return;
    const header = document.querySelector('.pwa-nav-top');
    const headerH = header ? header.getBoundingClientRect().height : 56;
    let isStuck = false;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const shouldStick = !entry.isIntersecting;
        if (shouldStick === isStuck) return;
        isStuck = shouldStick;
        if (shouldStick) {
          const mb = parseFloat(window.getComputedStyle(bar).marginBottom) || 0;
          placeholder.style.height = `${bar.offsetHeight + mb}px`;
          bar.classList.add('is-stuck');
        } else {
          bar.classList.remove('is-stuck');
          placeholder.style.height = '0px';
        }
      },
      { rootMargin: `-${Math.ceil(headerH)}px 0px 0px 0px`, threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Refetch when accessToken or visibility changes (e.g. after re-login) so list is not empty
  useEffect(() => {
    if (!accessToken) return;
    if (isVisible) fetchFavorites();
  }, [accessToken, isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    const handleFavoritesUpdated = () => {
      void fetchFavorites();
    };
    window.addEventListener('talentfunn:favorites-updated', handleFavoritesUpdated);
    return () => {
      window.removeEventListener('talentfunn:favorites-updated', handleFavoritesUpdated);
    };
  }, [isVisible, accessToken]);

  const fetchFavorites = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/favorites`);

      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      devLog('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (userId: string) => {
    try {
      const response = await fetchWithAuth(`${API_BASE}/favorites/toggle/${userId}`, { method: 'POST' });

      if (response.ok) {
        // Remove from local state
        setFavorites(favorites.filter((fav) => fav.id !== userId));
        window.dispatchEvent(new Event('talentfunn:favorites-updated'));
      } else {
        const errorData = await response.json();
        handleApiError(errorData, () => {
          onNavigateToSubscription?.();
        });
      }
    } catch (error) {
      devLog('Error removing favorite:', error);
    }
  };

  const avatarColors = [
    'from-purple-500 to-pink-500',
    'from-blue-500 to-cyan-500',
    'from-orange-500 to-red-500',
    'from-green-500 to-emerald-500',
  ];
  
  const getAvatarColor = (name: string) => {
    const index = name?.charCodeAt(0) % avatarColors.length || 0;
    return avatarColors[index];
  };

  return (
    <div className="min-h-screen xl:min-h-0 xl:h-full flex flex-col bg-white xl:bg-gradient-to-br xl:from-purple-50 xl:via-pink-50 xl:to-blue-50 xl:overflow-hidden">
      {!hideHeader && <div ref={stickySentinelRef} className="h-0 w-full" aria-hidden="true" />}
      {!hideHeader && (
        <>
          <div ref={stickyHeaderRef} className="sub-page-header bg-white border-b border-gray-200 px-4 pt-4 pb-3 flex items-center gap-3 flex-shrink-0 sticky top-0 z-20">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex-1 flex items-center gap-3">
              <Heart className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div>
                <h2 className="font-semibold">Mine favoritter</h2>
                <p className="text-sm text-gray-600">
                  {loading ? '...' : `${favorites.length} ${favorites.length === 1 ? 'talent' : 'talenter'} lagret`}
                </p>
              </div>
            </div>
          </div>
          <div ref={stickyPlaceholderRef} style={{ height: 0 }} aria-hidden="true" />
        </>
      )}

      {/* Content */}
      <div ref={subPageContentRef} className="sub-page-content flex-1 xl:min-h-0 xl:overflow-y-auto">
        <PageShell className="px-4 py-4 xl:max-w-4xl xl:mx-auto xl:py-6">
          {hideHeader && (
            <div className="flex items-center gap-3 pb-4 mb-4 border-b border-gray-200">
              <Heart className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div>
                <h2 className="font-semibold text-gray-900">Mine favoritter</h2>
                <p className="text-sm text-gray-600">
                  {loading ? '...' : `${favorites.length} ${favorites.length === 1 ? 'talent' : 'talenter'} lagret`}
                </p>
              </div>
            </div>
          )}
          {loading ? (
            <div className="text-center py-12">
              <Loader size="xl" className="text-purple-600" />
              <p className="mt-4 text-purple-900">Laster favoritter...</p>
            </div>
          ) : (
            <>
            {/* Favorites Grid */}
            <div className="pb-8">
              {favorites.length === 0 ? (
                <div className="bg-white p-8 text-center xl:rounded-3xl xl:shadow-lg xl:border-2 xl:border-purple-100 xl:p-12">
                  <Heart className="w-20 h-20 text-gray-300 mx-auto mb-6" />
                  <h2 className="text-2xl text-gray-600 mb-4">Ingen favoritter ennå</h2>
                  <p className="text-gray-500 mb-8">
                    Når du finner talenter du er interessert i, kan du legge dem til som favoritter for enkel tilgang senere.
                  </p>
                  <button
                    onClick={onExploreTalents ?? onBack}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all font-semibold"
                  >
                    Utforsk talenter
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 xl:grid-cols-2 2xl:grid-cols-3 gap-4 xl:gap-6">
                  {favorites.map((talent) => (
                    <div
                      key={talent.id}
                      className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 xl:rounded-3xl xl:shadow-lg xl:border-2 xl:border-purple-100 hover:shadow-xl transition-all group"
                    >
                      {/* Cover gradient - Same as profile view */}
                      <div className="h-24 bg-gradient-to-r from-purple-600 to-pink-600" />
                      
                      <div className="px-6 pb-6 -mt-12">
                        {/* Avatar */}
                        {talent.profileImage ? (
                          <img
                            src={talent.profileImage}
                            alt={talent.name}
                            className="w-24 h-24 rounded-full border-4 border-white shadow-xl mb-4 object-cover"
                          />
                        ) : (
                          <div className={`w-24 h-24 bg-gradient-to-br ${getAvatarColor(talent.name || 'A')} rounded-full flex items-center justify-center border-4 border-white shadow-xl mb-4`}>
                            {talent.name?.charAt(0) ? (
                              <span className="text-4xl text-white">
                                {talent.name.charAt(0).toUpperCase()}
                              </span>
                            ) : (
                              <User className="w-12 h-12 text-white" />
                            )}
                          </div>
                        )}

                        {/* Name and badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-xl bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent pb-1">
                            {talent.name || 'Ukjent'}
                          </h3>
                          {canUploadVideo(talent) && (
                            <div className="flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full">
                              <Crown className="w-3 h-3 text-white" />
                              <span className="text-white text-xs">PLUSS</span>
                            </div>
                          )}
                        </div>

                        {/* Location */}
                        {(talent.location || talent.county) && (
                          <div className="flex items-center gap-2 text-gray-600 mb-3">
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm">
                              {[talent.location, talent.county].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}

                        {/* Bio */}
                        {talent.bio && (
                          <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                            {talent.bio}
                          </p>
                        )}

                        {/* Skills */}
                        {talent.skills && talent.skills.length > 0 && (
                          <div className="mb-4">
                            <div className="flex flex-wrap gap-2">
                              {talent.skills.slice(0, 3).map((skill: string, index: number) => (
                                <span
                                  key={index}
                                  className="px-3 py-1 bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 rounded-lg text-xs border border-purple-200"
                                >
                                  {skill}
                                </span>
                              ))}
                              {talent.skills.length > 3 && (
                                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                                  +{talent.skills.length - 3} flere
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Action buttons */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => onViewProfile(talent.id)}
                            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:shadow-md transition-all text-sm"
                          >
                            Se profil
                          </button>
                          <button
                            onClick={() => removeFavorite(talent.id)}
                            className="px-4 py-2 bg-red-500 text-white rounded-full hover:shadow-md transition-all text-sm"
                            title="Fjern fra favoritter"
                          >
                            <Heart className="w-4 h-4 fill-current" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </>
          )}
        </PageShell>
      </div>
    </div>
  );
}
